class AddonProduct extends HTMLElement {
  constructor() {
    super();
    this.selections = {}; // group -> { id, name, price }
    this.selectedDesign = null; // { name, image, sizeType, size }
  }

  connectedCallback() {
    this.totalPriceEl = this.querySelector('[data-total-price]');
    this.basePrice = parseInt(this.totalPriceEl?.dataset.basePrice || 0, 10);

    this.ringSection = this.querySelector('#ring_section');
    this.pendantSection = this.querySelector('#pendant_section');

    // Two separate metal boxes — one for Ring, one for Pendant.
    // Only the one matching the selected product type is shown/used.
    this.ringMetalSection = this.querySelector('[data-addon-section="metal"][data-metal-for="ring"]');
    this.pendantMetalSection = this.querySelector('[data-addon-section="metal"][data-metal-for="pendant"]');

    this.sizeTypeSelect = this.querySelector('[data-design-field="sizeType"]');
    this.sizeSelect = this.querySelector('select[data-design-field="size"]');

    this.captureMoneyFormat();
    this.paintMetalSwatches();

    this.initTileGroup('[data-addon-group="product-type"]', '.addon-tile', 'product-type', (tile) => {
      this.toggleDesignSection(tile.dataset.name);
      const labelValue = this.querySelector('[data-label-value]');
      if (labelValue) labelValue.textContent = tile.dataset.name.toUpperCase();
      this.syncProductTypeToMainForm(tile.dataset.name);
      this.syncDesignToMainForm();
    });

    // Initialize each metal swatch group separately (Ring box + Pendant box)
    if (this.ringMetalSection) {
      this.initTileGroup(
        '[data-addon-section="metal"][data-metal-for="ring"]',
        '.addon-swatch',
        'metal'
      );
    }
    if (this.pendantMetalSection) {
      this.initTileGroup(
        '[data-addon-section="metal"][data-metal-for="pendant"]',
        '.addon-swatch',
        'metal'
      );
    }

    this.initTileGroup('[data-addon-group="certification"]', '.addon-card', 'certification');
    this.initSizeField();
    this.initDesignRadios();
    this.initSizeTypeField();

    // Show correct design section for whichever tile is selected by default,
    // and make sure the Product Type property reflects it too.
    const initialTile = this.querySelector('.addon-tile.is-selected');
    if (initialTile) {
      this.toggleDesignSection(initialTile.dataset.name);
      this.syncProductTypeToMainForm(initialTile.dataset.name);
    }

     this.bindVariantPriceSync(); // <-- add near the end, before updateTotal()

    this.updateTotal();
    this.syncDesignToMainForm();
  }

  captureMoneyFormat() {
    const initialText = (this.totalPriceEl?.textContent || '').trim();
    const match = initialText.match(/^([^\d]*)[\d.,]+([^\d]*)$/);
    this.moneyPrefix = match ? match[1] : '$';
    this.moneySuffix = match ? match[2] : '';
  }

  // Give each metal swatch circle a colour based on its name (White/Yellow/Rose/Platinum/Copper...)
  paintMetalSwatches() {
    this.querySelectorAll('[data-metal-swatch]').forEach((el) => {
      const name = (el.dataset.metalSwatch || '').toLowerCase();
      let color = '#e5e7eb'; // default / platinum

      if (name.includes('yellow')) color = '#d4af37';
      else if (name.includes('rose')) color = '#d69a8d';
      else if (name.includes('copper')) color = '#b87333';
      else if (name.includes('panchdhatu')) color = '#c9a66b';
      else if (name.includes('ashtdhatu')) color = '#8c7853';
      else if (name.includes('white') || name.includes('platinum') || name.includes('silver')) {
        color = '#d4d7db';
      }

      el.style.background = color;
    });
  }

  // Generic single-select tile/swatch/card group.
  // wrapperSelector should uniquely identify ONE container (e.g. include data-metal-for
  // for the metal groups, since ring and pendant boxes share the same data-addon-group="metal").
  initTileGroup(wrapperSelector, itemSelector, group, onSelect) {
    const wrapper = this.querySelector(wrapperSelector);
    if (!wrapper) return;

    const items = wrapper.querySelectorAll(itemSelector);
    items.forEach((item) => {
      if (item.classList.contains('is-selected')) {
        this.setSelectionFromTile(group, item);
      }

      item.addEventListener('click', () => {
        items.forEach((i) => i.classList.remove('is-selected'));
        item.classList.add('is-selected');
        this.setSelectionFromTile(group, item);
        this.updateTotal();
        if (typeof onSelect === 'function') onSelect(item);
      });
    });
  }

  toggleDesignSection(typeName) {
    const name = (typeName || '').trim().toLowerCase();

    if (this.ringSection) this.ringSection.hidden = name !== 'ring';
    if (this.pendantSection) this.pendantSection.hidden = name !== 'pendant';

    // Show only the metal box matching the selected product type; hide the other.
    if (this.ringMetalSection) this.ringMetalSection.hidden = name !== 'ring';
    if (this.pendantMetalSection) this.pendantMetalSection.hidden = name !== 'pendant';

    this.syncMetalSelection(name);

    this.captureSelectedDesign();
    this.updateTotal();
  }

  syncMetalSelection(name) {
    const activeMetalSection =
      name === 'ring' ? this.ringMetalSection :
      name === 'pendant' ? this.pendantMetalSection :
      null;

    if (!activeMetalSection) {
      delete this.selections['metal'];
      return;
    }

    const selectedSwatch = activeMetalSection.querySelector('.addon-swatch.is-selected');
    if (selectedSwatch) {
      this.setSelectionFromTile('metal', selectedSwatch);
    } else {
      delete this.selections['metal'];
    }
  }

  // Ring & pendant design radio buttons
  initDesignRadios() {
    this.querySelectorAll('input[name="ring_design"], input[name="pendant_design"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        const frame = radio.closest('.design-tile');
        radio.closest('.addon-tiles')?.querySelectorAll('.design-tile').forEach((t) => t.classList.remove('is-selected'));
        if (frame) frame.classList.add('is-selected');
        this.captureSelectedDesign();
        this.syncDesignToMainForm();
      });
    });

    this.captureSelectedDesign();
  }

  // Size select dropdown (only relevant while ring_section is visible)
  initSizeField() {
    if (!this.sizeSelect) return;
    this.sizeSelect.addEventListener('change', () => {
      this.captureSelectedDesign();
      this.syncDesignToMainForm();
    });
  }

  initSizeTypeField() {
    if (!this.sizeTypeSelect) return;
    this.sizeTypeSelect.addEventListener('change', () => {
      this.captureSelectedDesign();
      this.syncDesignToMainForm();
    });
  }

  captureSelectedDesign() {
  let activeRadio = null;
  let includeSize = false;

  if (this.ringSection && !this.ringSection.hidden) {
    activeRadio = this.ringSection.querySelector('input[name="ring_design"]:checked');
    includeSize = true;
  } else if (this.pendantSection && !this.pendantSection.hidden) {
    activeRadio = this.pendantSection.querySelector('input[name="pendant_design"]:checked');
  }

  // Design radio na ho (jaise ring-product template pe) tab bhi,
  // agar size fields dikh rahe hain to unhe capture karna hai.
  if (!activeRadio && !includeSize) {
    this.selectedDesign = null;
    this.updateSummary();
    return;
  }

  this.selectedDesign = {
    name: activeRadio ? activeRadio.value.trim() : '',
    image: activeRadio ? (activeRadio.dataset.image || '') : ''
  };

  if (includeSize) {
    if (this.sizeTypeSelect?.value) this.selectedDesign.sizeType = this.sizeTypeSelect.value;
    if (this.sizeSelect?.value) this.selectedDesign.size = this.sizeSelect.value;
  }

  this.updateSummary();
}

  syncDesignToMainForm() {
    const sectionId = this.dataset.section;

    this.setHiddenValue(`design-property-${sectionId}`, this.selectedDesign?.name || '');
    this.setHiddenValue(`design-image-property-${sectionId}`, this.selectedDesign?.image || '');
    this.setHiddenValue(`size-type-property-${sectionId}`, this.selectedDesign?.sizeType || '');
    this.setHiddenValue(`size-property-${sectionId}`, this.selectedDesign?.size || '');
  }

  // Writes the selected "Choose product type" tile's name into the
  // properties[Product Type] hidden input tied to the main product form,
  // so it shows up as a line item property in cart/checkout/orders.
  syncProductTypeToMainForm(name) {
    const sectionId = this.dataset.section;
    this.setHiddenValue(`product-type-property-${sectionId}`, name || '');
  }

  setHiddenValue(id, value) {
    const input = document.getElementById(id);
    if (input) input.value = value;
  }

  setSelectionFromTile(group, tile) {
    this.selections[group] = {
      id: tile.dataset.variantId,
      name: tile.dataset.name,
      price: parseInt(tile.dataset.price || 0, 10)
    };
  }

  // Formats cents into a money string using Shopify's format when available,
  // and strips a trailing ".00" so whole-number prices display cleanly
  // (e.g. "Rs. 42,500.00" -> "Rs. 42,500"), while keeping real decimals
  // like ".50" intact.
  formatMoney(cents) {
    let formatted;
    if (window.Shopify?.formatMoney && Shopify.money_format) {
      formatted = Shopify.formatMoney(cents, Shopify.money_format).replace(/^US\$/, '$');
    } else {
      const amount = (cents / 100).toFixed(2);
      const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d)\.)/g, ',');
      formatted = `${this.moneyPrefix}${withCommas}${this.moneySuffix}`;
    }
    return formatted.replace(/\.00(?=\D*$)/, '');
  }

  
  updateTotal() {
    const addonsTotal = Object.values(this.selections).reduce((sum, s) => sum + s.price, 0);
    const total = this.basePrice + addonsTotal;
    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatMoney(total);
    }
    this.updateSummary();
  }
  // Keeps basePrice in sync with whatever variant the customer picks
// (carat/ratti, size, etc.), since variant-set-option only updates the
// MAIN product price display, not this widget's total.
bindVariantPriceSync() {
  const sectionId = this.dataset.section;

  // Find the product form tied to this widget's section.
  this.variantForm =
    document.querySelector(`#product-form-${sectionId}`) ||
    this.totalPriceEl?.closest('form') ||
    document.querySelector('form[action*="/cart/add"]');

  this.variantIdInput = this.variantForm?.querySelector('input[name="id"], select[name="id"]');
  if (!this.variantIdInput) return;

  // Cache product.js JSON once so we can look up variant price by id
  // without a network call on every change.
  this.productJsonPromise = this.fetchProductJson(sectionId);

  // Case 1: the input is a real <select> or the value is set via a normal
  // user interaction that fires 'change' — this covers most themes.
  this.variantIdInput.addEventListener('change', () => {
    this.syncBasePriceFromVariantId(this.variantIdInput.value);
  });

  // Case 2: many themes update input[name="id"].value PROGRAMMATICALLY
  // (via JS, not a real user event), which does NOT fire 'change'.
  // Watch the value attribute directly to catch that too.
  const observer = new MutationObserver(() => {
    this.syncBasePriceFromVariantId(this.variantIdInput.value);
  });
  observer.observe(this.variantIdInput, { attributes: true, attributeFilter: ['value'] });

  // Case 3: also poll on any click inside variant-set-option, since some
  // themes swap the id input value via a microtask/timeout after click.
  document.querySelectorAll('variant-set-option').forEach((el) => {
    el.addEventListener('click', () => {
      setTimeout(() => this.syncBasePriceFromVariantId(this.variantIdInput.value), 150);
    });
  });
}

async fetchProductJson(sectionId) {
  const container = this.closest('[data-product-handle]') || document.querySelector('variant-set-option');
  const productUrl = container?.dataset.url;
  if (!productUrl) return null;

  try {
    const res = await fetch(`${productUrl}.js`);
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}

async syncBasePriceFromVariantId(variantId) {
  if (!variantId) return;
  const product = await this.productJsonPromise;
  if (!product) return;

  const variant = product.variants.find((v) => String(v.id) === String(variantId));
  if (!variant) return;

  if (this.basePrice !== variant.price) {
    this.basePrice = variant.price; // Shopify variant.price is already in cents
    if (this.totalPriceEl) this.totalPriceEl.dataset.basePrice = variant.price;
    this.updateTotal();
  }
}
  updateSummary() {
    const typeEl = this.querySelector('[data-summary-type]');
    const metalEl = this.querySelector('[data-summary-metal]');
    const designEl = this.querySelector('[data-summary-design]');
    const sizeEl = this.querySelector('[data-summary-size]');
    const certEl = this.querySelector('[data-summary-certificate]');
    const metalRow = this.querySelector('[data-summary-row-metal]');
    const designRow = this.querySelector('[data-summary-row-design]');
    const sizeRow = this.querySelector('[data-summary-row-size]');

    if (typeEl) typeEl.textContent = this.selections['product-type']?.name || 'Gemstone';

    const isGemstone = !this.selections['metal'];
    if (metalRow) metalRow.hidden = isGemstone;
    if (metalEl && !isGemstone) metalEl.textContent = this.selections['metal'].name;

    const hasDesign = !!this.selectedDesign;
    if (designRow) designRow.hidden = !hasDesign;
    if (designEl && hasDesign) {
      const designLabel = (this.selectedDesign.name || '').split('|')[1] || this.selectedDesign.name;
      designEl.textContent = designLabel;
    }

    const hasSize = hasDesign && this.selectedDesign.size;
    if (sizeRow) sizeRow.hidden = !hasSize;
    if (sizeEl && hasSize) {
      const { size, sizeType } = this.selectedDesign;
      sizeEl.textContent = sizeType ? `${size} (${sizeType})` : `${size}`;
    }

    if (certEl) certEl.textContent = this.selections['certification']?.name || '—';
  }

  hasSelections() {
    return Object.keys(this.selections).length > 0;
  }

  getSelectedItems() {
    return Object.values(this.selections).filter((s) => s.id);
  }

  getSelectedDesign() {
    return this.selectedDesign;
  }
}

if (!customElements.get('addon-product')) {
  customElements.define('addon-product', AddonProduct);
}


